import { TaskDataTable } from "@/components/task/data-table";
import { priorities, statuses, types } from "@/components/task/utils";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { DatePicker } from "@/components/ui/date-picker";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";
import { Form, FormControl, FormField, FormItem, FormLabel, FormMessage } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Textarea } from "@/components/ui/textarea";
import { useUser } from "@/providers/user";
import { DataService } from "@/services/data";
import { PaginatedResponse } from "@/types/common";
import { TaskType } from "@/types/user";
import { getTasksEndpointForObject } from "@/utils/content-types";
import { zodResolver } from "@hookform/resolvers/zod";
import { Loader2, LoaderCircle, Plus } from "lucide-react";
import { DateTime } from "luxon";
import { useEffect, useState } from "react";
import { useForm } from "react-hook-form";
import { useSearchParams } from "react-router-dom";
import { z } from "zod";

interface GenericTaskListProps {
    /** CRM object type (e.g. 'opportunities', 'accounts', 'contacts') */
    objectType: string;
    /** Object ID to filter tasks */
    objectId: number;
    /** Optional title for the card */
    title?: string;
    /** Display in compact mode or not */
    compact?: boolean;
}

const taskSchema = z.object({
    title: z.string().min(1, "Title is required"),
    description: z.string().optional(),
    due_date: z.string().optional(), // Store as ISO string for API
    status: z.enum(["backlog", "todo", "in_progress", "done", "canceled"]).default("backlog"),
    priority: z.enum(["low", "medium", "high"]).default("low"),
    type: z.enum(["note", "call", "meeting", "email"]).default("note")
});

type TaskFormData = z.infer<typeof taskSchema>;

/**
 * Generic component to display and manage tasks linked to any object
 * Uses the new /tasks endpoint available on all CRM objects
 */
export function GenericTaskList({ objectType, objectId, title = "Tasks", compact = true }: GenericTaskListProps) {
    const { user } = useUser();
    const [searchParams] = useSearchParams();
    const [tasks, setTasks] = useState<TaskType[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [isDialogOpen, setIsDialogOpen] = useState(false);
    const [isSubmitting, setIsSubmitting] = useState(false);

    const form = useForm<TaskFormData>({
        resolver: zodResolver(taskSchema),
        defaultValues: {
            title: "",
            description: "",
            status: "backlog",
            priority: "low",
            type: "note"
        }
    });

    const fetchTasks = async () => {
        try {
            setIsLoading(true);

            // Build URL with all current search parameters for filtering
            const endpoint = getTasksEndpointForObject(objectType, objectId);
            const params = new URLSearchParams();

            // Copy all search params to include filters
            searchParams.forEach((value, key) => {
                params.append(key, value);
            });

            // Ensure we have reasonable pagination
            if (!params.has("page_size")) {
                params.set("page_size", "1000");
            }

            const fullUrl = `${endpoint}?${params.toString()}`;
            const tasksResponse = (await DataService.client.url(fullUrl).get().json()) as PaginatedResponse<TaskType>;

            setTasks(tasksResponse.results || []);
        } catch (error) {
            console.error("Error fetching tasks:", error);
            setTasks([]);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        if (objectType && objectId) {
            fetchTasks();
        }
    }, [objectType, objectId, searchParams]); // Re-fetch when search params change

    const onSubmit = async (data: TaskFormData) => {
        try {
            setIsSubmitting(true);

            // Prepare data for API - due_date already in ISO format
            const taskData = {
                ...data,
                due_date: data.due_date || null
                // Backend will automatically determine reference_type and reference_id
                // from the endpoint used
            };

            // Create task via the new POST endpoint specific to the object
            // which handles reference_type and reference_id automatically
            const endpoint = getTasksEndpointForObject(objectType, objectId);
            await DataService.client.url(endpoint).post(taskData).json();

            // Refresh task list
            await fetchTasks();

            // Close dialog and reset form
            setIsDialogOpen(false);
            form.reset();
        } catch (error) {
            console.error("Error creating task:", error);
        } finally {
            setIsSubmitting(false);
        }
    };

    if (isLoading) {
        return (
            <div className="flex items-center justify-center p-6">
                <LoaderCircle className="h-6 w-6 animate-spin" />
            </div>
        );
    }

    const content = (
        <div className="space-y-4">
            <div className="flex items-center justify-end">
                <Dialog open={isDialogOpen} onOpenChange={setIsDialogOpen}>
                    <DialogTrigger asChild>
                        <Button size="sm" variant="outline">
                            <Plus className="mr-1 h-4 w-4" />
                            Add
                        </Button>
                    </DialogTrigger>
                    <DialogContent className="sm:max-w-[600px]">
                        <DialogHeader>
                            <DialogTitle>Create new task</DialogTitle>
                        </DialogHeader>
                        <Form {...form}>
                            <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4">
                                <FormField
                                    control={form.control}
                                    name="title"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Title *</FormLabel>
                                            <FormControl>
                                                <Input {...field} placeholder="Task title" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <FormField
                                    control={form.control}
                                    name="description"
                                    render={({ field }) => (
                                        <FormItem>
                                            <FormLabel>Description</FormLabel>
                                            <FormControl>
                                                <Textarea {...field} placeholder="Task description" />
                                            </FormControl>
                                            <FormMessage />
                                        </FormItem>
                                    )}
                                />

                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="status"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Status</FormLabel>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {statuses.map(status => (
                                                            <SelectItem key={status.value} value={status.value}>
                                                                {status.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="priority"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Priority</FormLabel>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {priorities.map(priority => (
                                                            <SelectItem key={priority.value} value={priority.value}>
                                                                {priority.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="grid grid-cols-2 gap-4">
                                    <FormField
                                        control={form.control}
                                        name="type"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Type</FormLabel>
                                                <Select onValueChange={field.onChange} defaultValue={field.value}>
                                                    <FormControl>
                                                        <SelectTrigger>
                                                            <SelectValue />
                                                        </SelectTrigger>
                                                    </FormControl>
                                                    <SelectContent>
                                                        {types.map(type => (
                                                            <SelectItem key={type.value} value={type.value}>
                                                                {type.label}
                                                            </SelectItem>
                                                        ))}
                                                    </SelectContent>
                                                </Select>
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />

                                    <FormField
                                        control={form.control}
                                        name="due_date"
                                        render={({ field }) => (
                                            <FormItem>
                                                <FormLabel>Due date</FormLabel>
                                                <DatePicker date={field.value ? DateTime.fromISO(field.value) : undefined} setDate={date => field.onChange(date?.toISODate() || "")} />
                                                <FormMessage />
                                            </FormItem>
                                        )}
                                    />
                                </div>

                                <div className="flex justify-end space-x-2">
                                    <Button type="button" variant="outline" onClick={() => setIsDialogOpen(false)} disabled={isSubmitting}>
                                        Cancel
                                    </Button>
                                    <Button type="submit" disabled={isSubmitting}>
                                        {isSubmitting ? (
                                            <>
                                                <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                                                Creating...
                                            </>
                                        ) : (
                                            "Create"
                                        )}
                                    </Button>
                                </div>
                            </form>
                        </Form>
                    </DialogContent>
                </Dialog>
            </div>

            <TaskDataTable tasks={tasks} is_manager={user?.is_manager ?? false} />
        </div>
    );

    if (compact) {
        return (
            <Card className="w-full">
                <CardHeader className="pb-3">
                    <CardTitle className="text-lg">{title}</CardTitle>
                </CardHeader>
                <CardContent>{content}</CardContent>
            </Card>
        );
    }

    return <div className="w-full">{content}</div>;
}
